/**
 * Organization Setup — create a new organization.
 *
 * Pre-fills legal name from tarjeta detection when navigated from the
 * listing wizard. Pre-fills display name from legal name.
 */
import { useState, useCallback } from 'react'
import { View, Text } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { CheckCircle, UserCheck } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { Select } from '@/components/ui/Select'
import { LegalEntityNameInput, SvNitInput } from '@/components/ui/StructuredTextInput'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { useCreateOrganization } from '@/lib/hooks/use-organization'
import { useProfile } from '@/lib/hooks/use-profile'
import { createOrganizationSchema } from '@/lib/validations/organization.schema'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { useSemanticColors, SPACING, RADIUS, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useAuth } from '@/lib/hooks/use-auth'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import {
  compactSvNit,
  getSvNitMissingDigits,
  isValidSvNit,
  SV_NIT_DIGIT_LENGTH,
} from '@/lib/utils/sv-nit'
import { normalizeLegalEntityName } from '@/lib/utils/sv-identity'
import { useEntrance } from '@/lib/hooks/use-entrance'

const INSURANCE_OPTIONS = [
  { value: 'platform', label: 'org.insurance_platform', disabled: true },
  { value: 'fleet', label: 'org.insurance_fleet' },
  { value: 'hybrid', label: 'org.insurance_hybrid', disabled: true },
]

export default function OrgSetupScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const { profile } = useProfile()
  const { legalName: prefillLegalName, source } = useLocalSearchParams<{
    legalName?: string
    source?: string
  }>()

  const createOrg = useCreateOrganization()
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill from tarjeta detection
  // Strip legal suffixes for a clean display name
  const toDisplayName = (legal: string) =>
    legal
      .replace(/,?\s*S\.?\s*A\.?\s*(?:DE\s+C\.?\s*V\.?)?\.?$/i, '')
      .replace(/,?\s*LTDA\.?$/i, '')
      .replace(/,?\s*S\.?\s*(?:DE\s+)?R\.?\s*L\.?$/i, '')
      .trim()
      .toLowerCase()
      .replace(/(?:^|\s|[-/])\S/g, (c) => c.toUpperCase())

  const initialLegalName = prefillLegalName ? normalizeLegalEntityName(prefillLegalName) : ''
  const [name, setName] = useState(initialLegalName ? toDisplayName(initialLegalName) : '')
  const [legalName, setLegalName] = useState(initialLegalName)
  const [taxId, setTaxId] = useState('')
  const [email, setEmail] = useState(user?.email ?? '')
  const [phone, setPhone] = useState('')
  const [insuranceModel, setInsuranceModel] = useState<string>('fleet')

  const clearError = (field: string) => setErrors((e) => ({ ...e, [field]: '' }))
  const profileName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
  const legalRepresentativeName =
    profile?.displayName || profileName || user?.email || t('common.you')
  const taxIdDigits = compactSvNit(taxId)
  const taxIdIsComplete = taxIdDigits.length === SV_NIT_DIGIT_LENGTH
  const taxIdIsValid = taxIdIsComplete && isValidSvNit(taxId)
  const taxIdHasLiveError = taxIdIsComplete && !taxIdIsValid
  const taxIdHelperText =
    taxIdDigits.length === 0
      ? t('org.tax_id_helper_empty')
      : taxIdIsComplete
        ? taxIdIsValid
          ? t('org.tax_id_helper_valid')
          : t('validation.org_tax_id_checksum')
        : t('org.tax_id_helper_progress', { count: getSvNitMissingDigits(taxId) })
  const taxIdHelperColor =
    errors.taxId || taxIdHasLiveError
      ? COLORS.coral.DEFAULT
      : taxIdIsValid
        ? colors.success
        : colors.textMuted

  const handleCreate = useCallback(async () => {
    const result = createOrganizationSchema.safeParse({
      name,
      legalName,
      taxId,
      email,
      phone: phone || undefined,
      insuranceModel,
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }

    try {
      await createOrg.mutateAsync(result.data)
      void notification(NotificationType.Success)
      toast.success(t('org.created_title'), { description: t('org.created_desc') })
      if (source === 'hosting_gate') {
        useListingWizardStore.getState().reset()
        router.replace('/hosting/new-listing/details' as never)
        return
      }
      router.back()
    } catch (err) {
      if (__DEV__) console.error('[OrgSetup] create', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('org.create_failed')))
      captureError(err, { screen: 'org_setup', action: 'create' })
    }
  }, [name, legalName, taxId, email, phone, insuranceModel, createOrg, router, source, t])

  const translatedInsuranceOptions = INSURANCE_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.label),
    disabled: o.disabled,
  }))

  return (
    <ScreenContainer variant="content" scroll={false}>
      {/* The screen used to nest its own padded ScrollView inside
          KeyboardAwareView's, which already applies `paddingHorizontal:
          SPACING.lg` — every margin on the page was doubled to 48pt and the
          inputs ran ~96px narrower than on organization/members/settings. One
          scroll view, one gutter. The title moves onto the back button's row so
          the section shares a single header layout; the centred hero block that
          repeated it (and broke "…en un solo / lugar.") is gone. */}
      <ScreenHeader
        title={t('org.create')}
        subtitle={t('org.create_desc')}
        backTestID="org.setup-back"
      />
      <KeyboardAwareView includeTopInset={false}>
        {/* Name */}
        <Animated.View collapsable={false} entering={enter(2)}>
          <TextInput
            label={t('org.name')}
            value={name}
            onChangeText={(v) => {
              setName(v)
              clearError('name')
            }}
            error={errors.name}
            placeholder={t('org.name_placeholder')}
            testID="org.name-input"
          />
        </Animated.View>

        {/* Legal Name */}
        {/* Every field already ships FORM_FIELD_GAP under itself, so the extra
            `marginTop: SPACING.md` that used to sit on each group made the
            field-to-field step 48pt while the card-to-field step was 16pt.
            One step for the whole form: FORM_FIELD_GAP, owned by the field
            above. Blocks that are NOT fields (the helper line, the legal
            representative card) top up to the same 32pt explicitly. */}
        <Animated.View collapsable={false} entering={enter(3)}>
          <LegalEntityNameInput
            label={t('org.legal_name')}
            value={legalName}
            onChangeText={(v) => {
              setLegalName(v)
              clearError('legalName')
            }}
            error={errors.legalName}
            placeholder={t('org.legal_name_placeholder')}
            testID="org.legal-name-input"
          />
          {prefillLegalName ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.xs,
                marginTop: SPACING.xs,
              }}
            >
              <CheckCircle size={13} color={colors.success} strokeWidth={2} />
              <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.success }}>
                {t('org.prefilled_from_tarjeta')}
              </Text>
            </View>
          ) : null}
        </Animated.View>

        {/* NIT */}
        <Animated.View collapsable={false} entering={enter(4)}>
          <SvNitInput
            label={t('org.tax_id')}
            value={taxId}
            onChangeText={(v) => {
              setTaxId(v)
              clearError('taxId')
            }}
            error={errors.taxId}
            placeholder={t('org.tax_id_placeholder')}
            accessibilityHint={t('org.tax_id_accessibility_hint')}
            testID="org.tax-id-input"
          />
          <Text
            accessibilityRole={taxIdHasLiveError ? 'alert' : undefined}
            style={{
              // The field carries its own FORM_FIELD_GAP bottom margin, so
              // the helper landed 32pt under the NIT input and only 16pt
              // above the next one — it read as the caption of the field
              // below it. Pull it back against its own field and re-add the
              // group gap underneath.
              marginTop: SPACING.sm - FORM_FIELD_GAP,
              marginBottom: FORM_FIELD_GAP - SPACING.sm,
              fontSize: 12,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: taxIdHelperColor,
            }}
          >
            {taxIdHelperText}
          </Text>
        </Animated.View>

        {/* Legal representative */}
        <Animated.View
          collapsable={false}
          entering={enter(5)}
          style={{
            // The NIT helper above already carries `FORM_FIELD_GAP - SPACING.sm`
            // below it; these 8pt complete the same 32pt step every field pair
            // uses. The bottom margin is the step the card owes the field under
            // it — without it the "Correo electrónico" label sat glued to the
            // card at 16pt while every other group was 48pt apart.
            marginTop: SPACING.sm,
            marginBottom: FORM_FIELD_GAP,
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
            flexDirection: 'row',
            gap: SPACING.sm,
            alignItems: 'flex-start',
          }}
          testID="org.legal-representative-card"
        >
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: RADIUS.full,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${COLORS.coral.DEFAULT}10`,
            }}
          >
            <UserCheck size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 13,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('org.legal_representative')}
            </Text>
            <Text
              style={{
                marginTop: 2,
                fontSize: 14,
                lineHeight: 20,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
              numberOfLines={2}
            >
              {legalRepresentativeName}
            </Text>
            <Text
              style={{
                marginTop: SPACING.xs,
                fontSize: 12,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: colors.textMuted,
              }}
            >
              {t('org.legal_representative_desc')}
            </Text>
          </View>
        </Animated.View>

        {/* Email */}
        <Animated.View collapsable={false} entering={enter(5)}>
          <TextInput
            label={t('org.email')}
            value={email}
            onChangeText={(v) => {
              setEmail(v)
              clearError('email')
            }}
            error={errors.email}
            keyboardType="email-address"
            autoCapitalize="none"
            placeholder={t('org.email_placeholder')}
            testID="org.email-input"
          />
        </Animated.View>

        {/* Phone */}
        <Animated.View collapsable={false} entering={enter(6)}>
          <TextInput
            label={`${t('org.phone')} (${t('common.optional')})`}
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
            placeholder={t('org.phone_placeholder')}
            testID="org.phone-input"
          />
        </Animated.View>

        {/* Insurance Model */}
        <Animated.View collapsable={false} entering={enter(7)}>
          <Select
            label={t('org.insurance_model')}
            value={insuranceModel}
            onChange={setInsuranceModel}
            options={translatedInsuranceOptions}
            testID="org.insurance-select"
          />
          <Text
            style={{
              marginTop: -SPACING.lg,
              fontSize: 12,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: colors.textMuted,
            }}
          >
            {t('listing.insurance_locked_notice')}
          </Text>
        </Animated.View>

        {/* Create Button */}
        <Animated.View collapsable={false} entering={enter(8)} style={{ marginTop: SPACING.xl }}>
          <Button
            title={source === 'hosting_gate' ? t('org.create_and_publish') : t('org.create')}
            onPress={handleCreate}
            loading={createOrg.isPending}
            variant="primary"
            size="lg"
            testID="org.create-submit"
          />
        </Animated.View>
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
